export default function HeroSection() {
  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="hero" aria-label="トップページ ファーストビュー" className="relative min-h-screen flex flex-col justify-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/5732b60d74e8c754634dcf4a4fefe91e.jpg"
          alt="岡山県南部の住宅"
          className="w-full h-full object-cover object-top"
          fetchPriority="high"
          decoding="sync"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/40 to-black/60"></div>
      </div>

      {/* Campaign Badge */}
      <div className="absolute top-24 md:top-28 right-4 md:right-16 z-10">
        <div className="bg-amber-500 text-white rounded-full w-28 h-28 md:w-36 md:h-36 flex flex-col items-center justify-center text-center rotate-6 shadow-lg">
          <span className="text-xs md:text-sm font-bold leading-tight">15周年記念</span>
          <span className="text-2xl md:text-3xl font-black leading-tight">980円</span>
          <span className="text-xs font-semibold leading-tight">初月お試し</span>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 w-full max-w-6xl mx-auto px-4 md:px-8 pt-24 pb-16">
        <div className="max-w-3xl">
          {/* Label */}
          <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm text-white text-xs md:text-sm px-4 py-1.5 rounded-full mb-6 border border-white/30">
            <i className="ri-map-pin-line text-amber-300"></i>
            <span>岡山 地域密着15年</span>
          </div>

          {/* Catchcopy */}
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-black text-white leading-tight mb-6" style={{ fontFamily: "'Noto Serif JP', serif" }}>
            <span className="block text-lg md:text-2xl font-bold text-amber-200 mb-2" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
              岡山市・倉敷市の住宅メンテナンス
            </span>
            「もう、家のことで<br />
            <span className="text-amber-300">悩まなくていい。」</span>
          </h1>

          <p className="text-base md:text-xl text-white/90 leading-relaxed mb-8 max-w-xl">
            庭の草むしり、害虫の心配、雨漏りの不安——<br />
            岡山の家のことは、私たちにまるごとおまかせください。<br />
            <span className="text-amber-200 font-semibold">月額9,800円</span>で、安心の暮らしをお届けします。
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
            {/* Mobile: tel link / Desktop: scroll to contact */}
            <a
              href="tel:0120847481"
              className="sm:hidden cursor-pointer whitespace-nowrap bg-amber-500 hover:bg-amber-600 text-white font-bold px-8 py-4 rounded-full text-base md:text-lg transition-all flex items-center justify-center gap-2"
              aria-label="0120-847-481に電話する"
            >
              <i className="ri-phone-fill"></i>
              今すぐ電話する（無料）
              <i className="ri-arrow-right-line"></i>
            </a>
            <button
              onClick={() => scrollTo('contact')}
              className="hidden sm:flex cursor-pointer whitespace-nowrap bg-amber-500 hover:bg-amber-600 text-white font-bold px-8 py-4 rounded-full text-base md:text-lg transition-all items-center justify-center gap-2"
              aria-label="無料相談・お見積もりフォームへ移動"
            >
              <i className="ri-phone-line"></i>
              無料相談・お見積もりはこちら
              <i className="ri-arrow-right-line"></i>
            </button>
            <button
              onClick={() => scrollTo('price')}
              className="cursor-pointer whitespace-nowrap bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white font-semibold px-8 py-4 rounded-full text-base md:text-lg transition-all border border-white/40"
            >
              料金プランを見る
            </button>
          </div>

          {/* Mobile sub-link: scroll to contact form */}
          <button
            onClick={() => scrollTo('contact')}
            className="sm:hidden cursor-pointer mt-2 inline-flex items-center gap-1.5 text-white/70 hover:text-white text-xs transition-colors"
          >
            <i className="ri-mail-line text-amber-300"></i>
            <span className="underline underline-offset-4 decoration-white/30">
              メールで相談する（24時間受付）
            </span>
          </button>

          {/* Area Link */}
          <button
            onClick={() => scrollTo('area')}
            className="cursor-pointer mt-4 inline-flex items-center gap-2 text-white/80 hover:text-amber-300 text-sm transition-colors group"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-map-pin-2-line text-amber-300 group-hover:text-amber-200"></i>
            </div>
            <span className="underline underline-offset-4 decoration-white/40 group-hover:decoration-amber-300">
              対応エリアを確認する（岡山市・倉敷市など岡山県南部）
            </span>
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-down-line text-xs"></i>
            </div>
          </button>
        </div>

        {/* Trust Bar */}
        <div className="mt-16 md:mt-20 grid grid-cols-3 gap-4 max-w-2xl">
          {[
            { icon: 'ri-building-line', num: '創業15年', sub: '地域に根ざした実績' },
            { icon: 'ri-award-line', num: '法人化10年', sub: '安定した組織体制' },
            { icon: 'ri-home-smile-line', num: '1,200件以上', sub: '施工・管理実績' },
          ].map((item) => (
            <div key={item.num} className="text-center">
              <div className="w-8 h-8 flex items-center justify-center mx-auto mb-1">
                <i className={`${item.icon} text-amber-300 text-xl`}></i>
              </div>
              <div className="text-white font-black text-lg md:text-2xl leading-tight">{item.num}</div>
              <div className="text-white/70 text-xs md:text-sm">{item.sub}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-1 text-white/60 animate-bounce">
        <span className="text-xs">スクロール</span>
        <i className="ri-arrow-down-line text-lg"></i>
      </div>
    </section>
  );
}
